import { ChevronRight, Package } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { EmptyState } from "@/components/empty-state"
import { Card, CardContent } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { OrderStatusBadge } from "@/features/orders/components/order-status-badge"
import { formatNumber } from "@/lib/format"
import { paths } from "@/routes/paths"
import type { CooperativeOrderEmbed } from "@/types/domain"

interface CooperativeOrdersTableProps {
  orders: CooperativeOrderEmbed[] | null | undefined
}

const headCellClass =
  "h-11 px-4 text-muted-foreground text-xs font-medium uppercase tracking-wide"
const bodyCellClass = "px-4 py-4"

export function CooperativeOrdersTable({
  orders,
}: CooperativeOrdersTableProps) {
  const { t } = useTranslation(["cooperatives", "common"])
  const navigate = useNavigate()
  const mt = t("units.mt", { ns: "common" })
  const list = orders ?? []

  return (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center gap-2 p-4">
        <Package className="size-5 text-muted-foreground" />
        <h2 className="text-base font-semibold">{t("orders.title")}</h2>
      </div>
      <Separator />

      {list.length === 0 ? (
        <CardContent className="p-0">
          <EmptyState title={t("orders.empty")} className="border-0 py-12" />
        </CardContent>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className={headCellClass}>
                  {t("orders.table.orderNumber")}
                </TableHead>
                <TableHead className={headCellClass}>
                  {t("orders.table.client")}
                </TableHead>
                <TableHead className={`${headCellClass} text-right`}>
                  {t("orders.table.quantity")}
                </TableHead>
                <TableHead className={`${headCellClass} text-right`}>
                  {t("orders.table.delivered")}
                </TableHead>
                <TableHead className={`${headCellClass} min-w-[140px]`}>
                  {t("orders.table.status")}
                </TableHead>
                <TableHead aria-hidden className={`${headCellClass} w-8`} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((order) => (
                <TableRow
                  key={String(order.id)}
                  className="cursor-pointer hover:bg-muted/60"
                  onClick={() => navigate(paths.orderDetail(order.id))}
                >
                  <TableCell className={`${bodyCellClass} font-medium`}>
                    {order.orderNumber ?? `#${order.id}`}
                  </TableCell>
                  <TableCell
                    className={`${bodyCellClass} max-w-[200px] truncate`}
                  >
                    {order.customer ?? "—"}
                  </TableCell>
                  <TableCell
                    className={`${bodyCellClass} text-right tabular-nums`}
                  >
                    {formatNumber(order.quantity ?? 0)} {mt}
                  </TableCell>
                  <TableCell
                    className={`${bodyCellClass} text-right tabular-nums`}
                  >
                    <div>
                      {formatNumber(order.deliveriesQuantity ?? 0)}/
                      {formatNumber(order.quantity ?? 0)} {mt}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t("orders.table.deliveriesCount", {
                        count: order.deliveries ?? 0,
                      })}
                    </div>
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <OrderStatusBadge status={order.status} />
                  </TableCell>
                  <TableCell className={bodyCellClass}>
                    <ChevronRight className="size-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  )
}
